import React, { useMemo, useState } from 'react';
import { getPlatformAndPromotion } from '../../../../Utils/constants';
import { 
  Box, 
  Button, 
  Card, 
  CardActions, 
  CardContent, 
  CardMedia, 
  Collapse, 
  Typography,
  Stack,
  Avatar,
  Chip,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  Badge,
  Zoom,
  Fade
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  CalendarMonth as CalendarMonthIcon,
  Today as TodayIcon,
  PlayArrow as PlayArrowIcon,
  Share as ShareIcon,
  Timeline as TimelineIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import CampaignTimelineEntry from './campaignTimelineEntry';
import useIsDesktop from '../../../../Hooks/useIsDesktop';

export default function CampaignTimelineCard({
  creator,
  expandedCreators,
  handleExpandClick,
  handleAddCreatorEvent,
  handleDeleteCreatorEvent,
  handleEditCreatorEvent,
  setCreatorTimelines,
  masterTimeline,
  creatorTimelines 
}) {
  const [isHovered, setIsHovered] = useState(false);
  const platPromos = useMemo(
    () => creator.videos.map(video => ({ 
      ...getPlatformAndPromotion(video.rateKey), 
      video: video 
    })), 
    [creator]
  );
  const isDesktop = useIsDesktop();
  
  const isExpanded = expandedCreators[creator.creator];
  const timelineEvents = creatorTimelines[creator.creator] || [];
  const hasEvents = timelineEvents.length > 0;
  
  // Calculate timeline stats
  const completedEvents = timelineEvents.filter(event => event.status === 'complete').length;
  const upcomingEvents = timelineEvents.filter(event => {
    const dueDate = new Date(event.projected_date);
    const today = new Date();
    return dueDate > today;
  }).length;
  
  return (
    <Zoom in timeout={300}>
      <Card 
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          borderRadius: 4,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: isHovered 
            ? '0 25px 50px rgba(0,0,0,0.15)' 
            : '0 10px 30px rgba(0,0,0,0.08)',
          overflow: 'visible',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: isHovered ? 'translateY(-8px)' : 'translateY(0px)',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: hasEvents 
              ? 'linear-gradient(90deg, #4CAF50 0%, #8BC34A 100%)'
              : 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '16px 16px 0 0',
            opacity: isHovered ? 1 : 0.7,
            transition: 'opacity 0.3s ease'
          }
        }}
      >
        {/* Main Content */}
        <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
          {/* Creator Avatar */}
          <Box sx={{ 
            position: 'relative',
            p: { xs: 2, md: 3 },
            display: 'flex',
            alignItems: 'center'
          }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                hasEvents ? (
                  <Avatar 
                    sx={{ 
                      width: 28, 
                      height: 28, 
                      bgcolor: '#4CAF50',
                      border: '3px solid white',
                      fontSize: '12px'
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 16 }} />
                  </Avatar>
                ) : (
                  <Avatar 
                    sx={{ 
                      width: 28, 
                      height: 28, 
                      bgcolor: '#FF9800',
                      border: '3px solid white',
                      fontSize: '12px'
                    }}
                  >
                    <ScheduleIcon sx={{ fontSize: 16 }} />
                  </Avatar>
                )
              }
            >
              <Avatar
                src={creator.pfphref || '/default_profile.png'}
                alt={creator.creator}
                sx={{
                  width: { xs: 80, md: 120 },
                  height: { xs: 80, md: 120 },
                  border: '4px solid rgba(103, 126, 234, 0.2)',
                  boxShadow: '0 8px 24px rgba(103, 126, 234, 0.2)',
                  transition: 'all 0.3s ease',
                  ...(isHovered && {
                    transform: 'scale(1.05)',
                    boxShadow: '0 12px 32px rgba(103, 126, 234, 0.3)'
                  })
                }}
              />
            </Badge>
          </Box>

          {/* Creator Info */}
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            minWidth: 0
          }}>
            <CardContent sx={{ 
              flex: 1, 
              py: { xs: 2, md: 3 },
              px: { xs: 1, md: 2 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minHeight: { xs: 'auto', md: 151 }
            }}>
              <Box sx={{ mb: 2 }}>
                <Typography 
                  variant="h5" 
                  fontWeight={700}
                  color="#2c3e50"
                  gutterBottom
                  sx={{
                    fontSize: { xs: '1.25rem', md: '1.5rem' }
                  }}
                >
                  {creator.display_name}
                </Typography>
                
                {/* Timeline Stats */}
                <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                  {hasEvents ? (
                    <>
                      <Chip
                        icon={<CheckCircleIcon />}
                        label={`${completedEvents} Complete`}
                        size="small"
                        color="success"
                        variant="filled"
                      />
                      <Chip
                        icon={<ScheduleIcon />}
                        label={`${upcomingEvents} Upcoming`}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    </>
                  ) : (
                    <Chip
                      icon={<TimelineIcon />}
                      label="No Timeline"
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>
              
              {/* Platform and Promotion Info */}
              <Box sx={{ 
                minHeight: 0,
                flex: 1,
                overflowY: 'auto'
              }}>
                <Stack spacing={1}>
                  {platPromos.map((video, index) => (
                    <Paper 
                      key={video.video.id}
                      elevation={0}
                      sx={{ 
                        p: 1.5,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, rgba(103, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                        border: '1px solid rgba(103, 126, 234, 0.1)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: 'linear-gradient(135deg, rgba(103, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
                          transform: 'translateX(4px)'
                        }
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar sx={{
                          width: 24,
                          height: 24,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          fontSize: '12px'
                        }}>
                          <PlayArrowIcon sx={{ fontSize: 14 }} />
                        </Avatar>
                        
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color="#2c3e50"
                            sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                          >
                            {video.platform} - {video.promotion}
                          </Typography>
                          
                          {video.video.crossPostingKeys.size > 0 && (
                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                              <ShareIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                +{video.video.crossPostingKeys.size} crossposts
                              </Typography>
                            </Stack>
                          )}
                        </Box>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            </CardContent>

            {/* Action Buttons */}
            <CardActions sx={{ 
              flexDirection: { xs: 'row', md: 'column' },
              justifyContent: { xs: 'center', md: 'center' },
              alignItems: 'center',
              p: 2,
              gap: 1
            }}>
              <Button
                onClick={() => handleExpandClick(creator.creator)}
                startIcon={
                  <ExpandMoreIcon 
                    sx={{
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease'
                    }}
                  />
                }
                variant="outlined"
                size={isDesktop ? "medium" : "small"}
                sx={{
                  borderRadius: 3,
                  borderColor: 'rgba(103, 126, 234, 0.3)',
                  color: '#667eea',
                  fontWeight: 600,
                  minWidth: isDesktop ? 140 : 'auto',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#667eea',
                    backgroundColor: 'rgba(103, 126, 234, 0.05)',
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                {isDesktop ? (
                  `${isExpanded ? 'Hide' : 'Show'} Timeline`
                ) : (
                  <CalendarMonthIcon />
                )}
              </Button>
              
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleAddCreatorEvent(creator.creator)}
                size={isDesktop ? "medium" : "small"}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: 3,
                  fontWeight: 600,
                  minWidth: isDesktop ? 140 : 'auto',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)'
                  }
                }}
              >
                {isDesktop ? 'Add Event' : <TodayIcon />}
              </Button>
            </CardActions>
          </Box>
        </Box>

        {/* Expandable Timeline Content */}
        <Collapse
          in={isExpanded}
          timeout="auto"
          unmountOnExit
        >
          <Divider sx={{ opacity: 0.1 }} />
          <CardContent sx={{ p: 3, pt: 2 }}>
            {hasEvents ? (
              <Fade in timeout={300}>
                <Stack spacing={2}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    mb: 2
                  }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar sx={{
                        width: 32,
                        height: 32,
                        background: 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)'
                      }}>
                        <TimelineIcon sx={{ fontSize: 18 }} />
                      </Avatar>
                      <Typography variant="h6" fontWeight={600} color="#2c3e50">
                        Timeline Events
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {timelineEvents.length} milestone{timelineEvents.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  
                  {timelineEvents.map((event, index) => (
                    <CampaignTimelineEntry
                      key={event.id}
                      event={event}
                      onEditField={(field, value) =>
                        handleEditCreatorEvent(creator.creator, index, field, value)
                      }
                      onDelete={() => handleDeleteCreatorEvent(creator.creator, index)}
                    />
                  ))}
                </Stack>
              </Fade>
            ) : (
              <Fade in timeout={300}>
                <Paper sx={{ 
                  p: 4, 
                  textAlign: 'center',
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #f8faff 0%, #f1f5ff 100%)',
                  border: '1px solid rgba(103, 126, 234, 0.1)'
                }}>
                  <Avatar sx={{ 
                    width: 56, 
                    height: 56, 
                    mx: 'auto', 
                    mb: 2,
                    backgroundColor: 'rgba(103, 126, 234, 0.1)',
                    color: '#667eea'
                  }}>
                    <ScheduleIcon sx={{ fontSize: 28 }} />
                  </Avatar>
                  
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Timeline Events
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 300, mx: 'auto' }}>
                    This creator doesn't have any timeline events yet. You can add events manually or apply the master timeline.
                  </Typography>
                  
                  <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => handleAddCreatorEvent(creator.creator)}
                      sx={{
                        borderRadius: 3,
                        borderColor: 'rgba(103, 126, 234, 0.3)',
                        color: '#667eea',
                        fontWeight: 600
                      }}
                    >
                      Add Manually
                    </Button>
                    
                    {masterTimeline.length > 0 && (
                      <Button
                        variant="contained"
                        startIcon={<AutoAwesomeIcon />}
                        onClick={() => {
                          setCreatorTimelines((prev) => ({
                            ...prev,
                            [creator.creator]: masterTimeline.map((event) => ({
                              ...event,
                              id: Date.now() + Math.random(),
                            })),
                          }));
                        }}
                        sx={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          borderRadius: 3,
                          fontWeight: 600,
                          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                        }}
                      >
                        Apply Master
                      </Button>
                    )}
                  </Stack>
                </Paper>
              </Fade>
            )}
          </CardContent>
        </Collapse>
      </Card>
    </Zoom>
  );
}